import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import { AlertBanner, Button, Card, Checkbox, Group, Loader, Stack, Text } from '@grovus/design-system'
import { axisCopy, DICT, DICTIONARY_COPY, refusalCopy, SETTINGS_COPY, type Lang } from '../i18n'
import {
  CompanyNotFoundError, CreativesRequestError, REQUEST_TIMEOUT_MS, type CreativesParams, type Decision,
  type DecisionOutcome, type MergeKeep, type TagChange, changedAxes, tagChange,
} from './api'
import { SelectionBar, type AssignNotice } from './SelectionBar'
import type { OpenChip } from './TagChips'
import { useDictionary } from './useDictionary'
import { createListReads, type ListReads } from './reads'
import { failureLine, type Axis, type CreateOutcome, type HypothesisValue, type WriteOutcome } from '../hypotheses/dictionaryApi'
import { CreativeDetailPanel } from './CreativeDetailPanel'
import { MergeDialog } from './MergeDialog'
import { distinctAssignments } from './merge'
import type { Bounds, CreativesResponse, Suggestion } from './contract'
import { addDays, matchesEcho, periodDays, periodKey, type Period } from '../lib/period'
import { thresholdsOf } from './confidence'
import { fill } from './format'
import { errorCopy, SHELL_FIXABLE } from './refusal'
import {
  UNASSIGNED, buildRows, byName, byVerdict, floor, observedTotalsOf, partialScopeOf, segment, sortRows, totalsOf, withShare,
  type Row, type SortKey,
} from './partition'
import { segmentCoverage } from '../lib/delta'
import type { AccountToggleProps } from './AccountToggles'
import { visibleColumns, type ColumnKey } from './columns'
import { csvFileName, exportCells, exportFileName, toCsv } from './csv'
import { toXlsx, XLSX_MIME } from './xlsx'
import { CreativesFilterBar, type FilterState, type ViewMode } from './CreativesFilterBar'
import { CreativesGrid } from './CreativesGrid'
import { CreativesTable } from './CreativesTable'
import { Definitions, Notes, ThinBanner, type Bucket } from './Notes'
import { EmptyState, type EmptyKind } from './EmptyState'
import { NoDeliveryLine } from './NoDeliveryLine'
import { ScopeNotices } from './ScopeNotices'
import { TotalsRow, type TotalsComparison } from './TotalsRow'
import { labelOf, sortLabels, type ViewContext } from './view'
import { sumMoney, type SpendShare } from './metrics'
import { downloadBlob, downloadText } from '../lib/download'
import { loadHiddenColumns, saveHiddenColumns } from '../lib/preferences'

export interface CreativesScreenProps {
  lang: Lang
  /** The active company. Every request names it, and a response is held under it. */
  companyId: string
  period: Period
  /** `null` is the company's default scope; `[]` makes no request at all. */
  accountIds: string[] | null
  /** Another screen landing here narrowed to one value. */
  bucket?: Bucket | null
  /** The two verdict Questions. */
  focus?: 'scale' | 'kill' | null
  /**
   * A saved view's sort and layout, applied once each time the object identity changes.
   * Hold it in state rather than passing a literal: a fresh object on every render would
   * re-apply the sort after every manual change and the headers would stop responding.
   */
  preset?: { sort: SortKey; dir: 'asc' | 'desc'; view: ViewMode } | null
  /** The active client's id: its change closes the open panel and nothing else. */
  rowScopeKey?: string
  onCompareHypotheses: () => void
  /**
   * Opens the company settings. Passed for an Owner or an Admin only, who can set the target
   * the no-target hint asks for (GRO-185); without it the hint never shows.
   */
  onOpenSettings?: () => void
  onWidenPeriod?: (period: Period) => void
  /** The picker's limits for the selection; a range cannot widen past `earliest`. */
  bounds?: Bounds | null
  /** The bounds a response carried, with the scope it was asked under. */
  onBounds?: (report: { scope: string; bounds: Bounds | null }) => void
  /** A body from the pre-GRO-65 server arrived, or it refused a period only the new one
   * answers: the shell falls back to the old contract. */
  onOldContract?: () => void
  /** The server refused the range asked under `scope`: the shell decides whether to reset it. */
  onPeriodRefused?: (report: { scope: string }) => void
  /**
   * Opens the period control. The shell passes it only while it holds bounds for the
   * selection on screen; without it the nothing-delivered state offers no button.
   */
  onOpenPeriod?: () => void
  /**
   * The reader changed the account selection and the request for it has not been sent yet.
   * The retained response answers the previous selection, so nothing read off it is offered.
   */
  settling?: boolean
  /** The list answered `404 not_found` for this company: the shell re-checks the session. */
  onCompanyNotFound?: (companyId: string) => void
  /** A refused range was replaced by the default preset; say so above the figures. */
  rangeRefused?: boolean
  /** Ask for the previous window of the same length. Off while the shell talks to a server
   * that predates it. */
  compare?: boolean
  /**
   * The shell's reporting scope key, held beside the response it asked for and handed
   * back with every count. Opaque here: the screen never reads it.
   */
  scope?: string
  /**
   * The signed-in user, whose hidden columns are remembered on this device. Absent, the
   * choice lives for as long as the screen does.
   */
  userId?: string
  /** The shell's account selection, for the filter bar's toggles and the refusal's reset. */
  accountToggles?: AccountToggleProps
  /**
   * The guest view's re-read moved the auth generation (GRO-83): the requests it aborted
   * and the open panel's are issued again with the same selections. Constant for a member.
   */
  reloadKey?: number
  /** The screen is hidden, not unmounted: an open detail panel answers no key meanwhile. */
  suspended?: boolean
  /**
   * Bulk assignment of a hypothesis (GRO-157), passed only to a reader whose role may
   * assign. The shell issues the write and holds it as pending for the company, so a
   * remounted screen still sees it; its success raises the reporting revision in
   * `reloadKey`, which is what refetches this screen.
   */
  assignment?: AssignmentProps
  /** Injected in tests; the real screen uses the global. */
  fetchImpl?: typeof fetch
  /**
   * The shell's shared list reads. A body this screen read before it was unmounted paints
   * first when it mounts again under the same scope; the read still goes out and replaces it.
   */
  reads?: ListReads
  /**
   * Filled with a function that starts this screen's list read for a period and the
   * shell's scope under that period (GRO-180). The shell calls it from a period click,
   * before the render, and the read effect after the render joins the request. Only the
   * screen knows the audience filter, which is part of the key.
   */
  periodPrefetch?: RefObject<((next: Period, scope: string) => void) | null>
}

export interface AssignmentProps {
  /** A write is out for this company, from this screen or from an earlier mount of it. */
  pending: boolean
  /** Sets or clears the idea, the visual or both (GRO-157, GRO-170). */
  onAssign: (companyId: string, creativeIds: string[], change: TagChange) => Promise<WriteOutcome<{ updated: number }>>
  /** Creates a value on `axis` from a typed name and assigns it, as one pending write (GRO-158). */
  onCreateAndAssign: (companyId: string, creativeIds: string[], name: string, axis: Axis) => Promise<CreateOutcome>
  /** Creates a value on `axis` from a typed name and assigns nothing: the selection bar's field holds it (GRO-172). */
  onCreateValue: (companyId: string, name: string, axis: Axis) => Promise<WriteOutcome<HypothesisValue>>
  /** Sets or clears the version letter of creatives that carry a hypothesis (GRO-159). */
  onSetVersion: (companyId: string, creativeIds: string[], version: string | null) => Promise<WriteOutcome<{ updated: number }>>
  /** Merges creatives into one row, keeping the chosen assignment when they differ (GRO-161). */
  onMerge: (companyId: string, creativeIds: string[], keep: MergeKeep | null) => Promise<WriteOutcome<{ creativeId: string }>>
  /** Accepts or rejects one open suggestion of a creative (GRO-173). */
  onDecide: (companyId: string, creativeId: string, suggestionId: string, decision: Decision) => Promise<DecisionOutcome>
  /** Moves after any dictionary write in this company: the picker reads its options again. */
  pickerRevision: number
}

const NOTHING_PICKED: ReadonlySet<string> = new Set()

type Load =
  | { status: 'loading' }
  | { status: 'ready' }
  | { status: 'error'; message: string; code?: string; accounts?: string[] }

const NO_FILTERS: FilterState = {
  hypothesisId: null,
  techniqueId: null,
  messageId: null,
  offerId: null,
  format: null,
  audienceId: null,
  minSpend: 0,
  minConversions: 0,
  showSamples: false,
  verdict: null,
  name: '',
}

/** How many narrowings the reader has set. The bucket note stands down at two. */
function narrowingCount(filters: FilterState): number {
  return [
    filters.hypothesisId,
    filters.techniqueId,
    filters.messageId,
    filters.offerId,
    filters.format,
    filters.audienceId,
    filters.verdict,
  ].filter((value) => value !== null).length + (filters.minSpend > 0 ? 1 : 0) + (filters.minConversions > 0 ? 1 : 0)
    + (filters.name.trim() !== '' ? 1 : 0)
}

/**
 * The creatives screen: everything below the shell's header.
 *
 * It owns fetching, the load states, the filters, the reading floor, the notes, the six
 * tiles, the table and the grid. It knows nothing about routes, the
 * sidebar or persistence, and it assumes it is rendered inside `GrovusProvider`.
 *
 * Account and audience changes are refetches, not filters: the server aggregates over
 * exactly the ads in scope, so a merged figure always describes the scope the reader is
 * looking at. Hypothesis, technique and media type are attributes of the asset and narrow
 * on the client.
 */
export function CreativesScreen({
  lang, companyId, period, accountIds,
  bucket = null, focus = null, preset = null, rowScopeKey,
  onCompareHypotheses, onOpenSettings, onWidenPeriod, bounds = null, onBounds, onOldContract, onPeriodRefused, onOpenPeriod, onCompanyNotFound,
  settling = false, rangeRefused = false, compare = false, scope = '', userId, accountToggles, reloadKey = 0, suspended = false,
  assignment, fetchImpl, reads, periodPrefetch,
}: CreativesScreenProps) {
  const t = DICT[lang]
  const own = useRef<ListReads | null>(null)
  const store = reads ?? (own.current ??= createListReads())
  const days = periodDays(period)
  const windowKey = periodKey(period)
  // The shell's callbacks, read through a ref so a new closure from the shell does not
  // refetch: they are not part of what the request asks.
  const shell = useRef({ onBounds, onOldContract, onPeriodRefused, onCompanyNotFound })
  shell.current = { onBounds, onOldContract, onPeriodRefused, onCompanyNotFound }

  // The retained body is held under the company it answers. A period or account change
  // keeps it on screen while the refetch runs; a company change must not, or the header
  // would name one company over another's figures for the length of the load.
  const [held, setHeld] = useState<{ companyId: string; body: CreativesResponse } | null>(null)
  const response = held !== null && held.companyId === companyId ? held.body : null
  // The company's tiers as this response echoes them (GRO-186): the ticks, the fold, the
  // columns, the totals and the notes read the tiers its verdicts were judged with.
  const thresholds = useMemo(() => thresholdsOf(response?.verdictBasis), [response])
  // What the displayed response actually answers. The screen keeps the previous body up
  // while a new request runs, and a failed request never clears it, so `response !== null`
  // says nothing about whether the rows describe the scope selected now. This does.
  const [servedFor, setServedFor] = useState<{ scope: string; audienceId: string | null } | null>(null)
  const [load, setLoad] = useState<Load>({ status: 'loading' })
  const [attempt, setAttempt] = useState(0)
  const [filters, setFilters] = useState<FilterState>(NO_FILTERS)
  const [view, setView] = useState<ViewMode>('table')
  const [sort, setSort] = useState<SortKey>('spend')
  const [dir, setDir] = useState<'asc' | 'desc'>('desc')
  const [selected, setSelected] = useState<{ id: string; share: SpendShare } | null>(null)
  // The survivor whose share the open panel takes from the first list after a merge, and
  // the merge dialog's state (GRO-161); both are read further down.
  const [shareFollows, setShareFollows] = useState<{ id: string; from: CreativesResponse } | null>(null)
  const [merging, setMerging] = useState<{ line: string | null; saving: boolean } | null>(null)
  // Hiding a column changes the columns and nothing else: nothing below the table reads
  // this set, so the sort, the totals and the counts cannot move with it.
  const [hiddenColumns, setHiddenColumns] = useState<ReadonlySet<ColumnKey>>(
    () => new Set(userId ? loadHiddenColumns(userId) : []),
  )
  const changeHiddenColumns = useCallback((next: ReadonlySet<ColumnKey>) => {
    setHiddenColumns(next)
    if (userId) saveHiddenColumns(userId, [...next])
  }, [userId])
  const visible = useMemo(() => visibleColumns(hiddenColumns), [hiddenColumns])
  // The audience values are fixed by Grovus (GRO-175), but the other company may hold no ad
  // in the selected one, so the scope still does not survive a company change: carried over,
  // it would answer `unknown_audience`. Cleared during render, before the fetch effect runs,
  // so the stale pair never reaches the wire.
  const [filtersCompany, setFiltersCompany] = useState(companyId)
  if (filtersCompany !== companyId) {
    setFiltersCompany(companyId)
    if (filters.audienceId !== null) setFilters((previous) => ({ ...previous, audienceId: null }))
  }

  const noSelection = accountIds !== null && accountIds.length === 0
  const accountKey = accountIds === null ? null : accountIds.join(',')

  // The read effect's parameters with the next period, so the prefetch and the read that
  // follows build one key. Refilled every render, as `shell` is; cleared on unmount only
  // while it still holds this screen's function.
  const prefetch = useRef<(next: Period, nextScope: string) => void>(() => {})
  prefetch.current = (next, nextScope) => {
    if (noSelection) return
    store.prefetch(
      { companyId, period: next, compare, accountIds, audienceId: filters.audienceId },
      { scope: nextScope, reloadKey },
      fetchImpl ?? fetch,
    )
  }
  const handed = useRef<((next: Period, nextScope: string) => void) | null>(null)
  handed.current ??= (next, nextScope) => prefetch.current(next, nextScope)
  if (periodPrefetch) periodPrefetch.current = handed.current
  useEffect(() => {
    if (!periodPrefetch) return
    const own = handed.current
    periodPrefetch.current = own
    return () => {
      if (periodPrefetch.current === own) periodPrefetch.current = null
    }
  }, [periodPrefetch])

  // A bucket sets one filter and clears every other narrowing, exactly as arriving from a
  // comparison should: the reader asked to see this bucket, not this bucket inside
  // whatever they had set before.
  const appliedBucket = useRef<string | null>(null)
  useEffect(() => {
    const key = bucket ? `${bucket.axis}:${bucket.id}` : null
    if (key === appliedBucket.current) return
    appliedBucket.current = key
    if (!bucket) return
    setFilters({
      ...NO_FILTERS,
      hypothesisId: bucket.axis === 'hypothesis' ? bucket.id : null,
      techniqueId: bucket.axis === 'technique' ? bucket.id : null,
      audienceId: bucket.axis === 'audience' ? bucket.id : null,
    })
  }, [bucket])

  // A focus orders the list by verdict: descending for Scale, ascending for Kill, as the
  // two Questions read. The design asks for a manual sort to clear it, and the screen
  // cannot: `focus` is a prop, so only its owner can put it down. A manual sort does
  // change the sort, so the list stops being ordered by verdict while the note still
  // claims it is. The owner is the Questions list, which GRO-18 brings; until then App
  // passes `focus: null` and this never happens in the product.
  const appliedFocus = useRef<string | null>(null)
  useEffect(() => {
    if (focus === appliedFocus.current) return
    appliedFocus.current = focus
    if (!focus) return
    setSort('verdict')
    setDir(focus === 'scale' ? 'desc' : 'asc')
  }, [focus])

  const appliedPreset = useRef<CreativesScreenProps['preset']>(undefined)
  useEffect(() => {
    if (preset === appliedPreset.current) return
    appliedPreset.current = preset
    if (!preset) return
    setSort(preset.sort)
    setDir(preset.dir)
    setView(preset.view)
  }, [preset])

  // A client change closes the open panel and nothing else: sort, filters and the
  // checkbox map survive it as GRO-20 left them. The key is the client's id and not the
  // account map, because a checkbox toggle must refetch the panel's split while a client
  // change must close it.
  const appliedRowScope = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (rowScopeKey === appliedRowScope.current) return
    appliedRowScope.current = rowScopeKey
    setSelected(null)
  }, [rowScopeKey])

  useEffect(() => {
    if (noSelection) {
      setLoad({ status: 'ready' })
      setHeld(null)
      setServedFor({ scope, audienceId: filters.audienceId })
      return
    }
    let current = true
    // A request the screen no longer wants is cancelled, not only ignored: a long window
    // over many accounts is the slowest read the server does, and a reader stepping through
    // selections would otherwise leave every one of them running.
    const controller = new AbortController()
    setLoad({ status: 'loading' })
    const params = { companyId, period, compare, accountIds, audienceId: filters.audienceId }
    const tag = { scope, reloadKey }
    // The body this pair was last answered with goes up before the read, so a return to
    // the screen paints at once. It passed the echo rule under these parameters when it was
    // read, checked again here because the rule is the screen's and not the store's.
    const kept = store.retained(params, tag)
    if (kept !== null && matchesEcho({ period, compare: kept.contract === 'old' ? false : compare }, kept)) {
      setHeld({ companyId, body: kept })
      setServedFor({ scope, audienceId: filters.audienceId })
    }
    store.read(
      params,
      tag,
      fetchImpl ?? fetch,
      AbortSignal.any([controller.signal, AbortSignal.timeout(REQUEST_TIMEOUT_MS)]),
    )
      .then((body) => {
        if (!current) return
        // The contract first, then the echo rule: a body is rendered only when it answers
        // the window and the comparison asked for. The old contract carries no comparison,
        // and its figures still answer a preset; a range it cannot answer is the shell's to
        // reset, and the refetch that follows answers it.
        const old = body.contract === 'old'
        if (old) shell.current.onOldContract?.()
        if (!matchesEcho({ period, compare: old ? false : compare }, body)) {
          if (!old) {
            setLoad({ status: 'error', message: `${periodKey(period)}: the response answers another window or comparison` })
          }
          return
        }
        setHeld({ companyId, body })
        setServedFor({ scope, audienceId: filters.audienceId })
        setLoad({ status: 'ready' })
        if (!old) shell.current.onBounds?.({ scope, bounds: body.bounds })
      })
      .catch((error: unknown) => {
        if (!current || (error instanceof DOMException && error.name === 'AbortError')) return
        if (error instanceof CompanyNotFoundError) shell.current.onCompanyNotFound?.(companyId)
        if (error instanceof CreativesRequestError && error.code === 'bad_period') {
          // Only the shell can change the period. A range this selection cannot answer
          // goes back to the default preset; a preset refused is one the server does not
          // know, which means the server predates it.
          if (period.kind === 'range') shell.current.onPeriodRefused?.({ scope })
          else shell.current.onOldContract?.()
        }
        if (error instanceof CreativesRequestError) {
          setLoad({
            status: 'error',
            message: error.message,
            code: error.code,
            accounts: error.accounts,
          })
          return
        }
        setLoad({
          status: 'error',
          message: error instanceof Error ? error.message : String(error),
        })
      })
    return () => {
      current = false
      controller.abort()
    }
    // `accountKey` and not `accountIds`: the shell builds a fresh array on every render,
    // so depending on the array itself would refetch on every keystroke in a filter. The
    // same set of accounts is the same request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, windowKey, compare, accountKey, filters.audienceId, attempt, noSelection, scope, reloadKey, fetchImpl])

  const ctx: ViewContext = useMemo(
    () => ({
      t,
      lang,
      currency: response?.currency ?? null,
      benchmarks: response?.benchmarks ?? { roas: null, cpa: null, ctr: null, hookRate: null, holdRate: null },
      thresholds,
      periodDays: days,
      dimensions: response?.dimensions ?? { hypotheses: [], techniques: [], messages: [], offers: [], audiences: [] },
      accounts: response?.accounts ?? [],
      // What the figures on screen are, read off the accounts that produced them rather
      // than off the company: a company can hold demo accounts beside real ones.
      isDemo: response !== null && response.accounts.length > 0 && response.accounts.every((a) => a.isDemo),
      basis: response?.verdictBasis ?? null,
    }),
    [t, lang, response, thresholds, days],
  )

  const all: Row[] = useMemo(
    () => (response ? buildRows(response.creatives, thresholds) : []),
    [response, thresholds],
  )
  // The attribute filters, the one narrowing the totals, the previous totals and the
  // coverage check all read, so the three describe the same creatives.
  const attributeFilters = useMemo(
    () => ({
      hypothesisId: filters.hypothesisId,
      techniqueId: filters.techniqueId,
      messageId: filters.messageId,
      offerId: filters.offerId,
      format: filters.format,
    }),
    [filters.hypothesisId, filters.techniqueId, filters.messageId, filters.offerId, filters.format],
  )
  // The segment-wide share, which the detail panel states. The table's column is
  // allocated again over the rows on screen, below.
  const inSegment = useMemo(() => withShare(segment(all, attributeFilters)), [all, attributeFilters])
  // The scope's own partial flag (D8), an input to the totals rather than a reading of the
  // rows, so no attribute filter can take it away.
  const partialScope = response !== null && partialScopeOf(response)
  const totals = useMemo(() => totalsOf(inSegment, partialScope, thresholds), [inSegment, partialScope, thresholds])
  const totalsComparison: TotalsComparison | null = useMemo(() => {
    const comparison = response?.comparison ?? null
    if (response === null || comparison === null) return null
    return {
      // The scope's marks sit on the current totals, and a delta withholds on either side.
      previous: observedTotalsOf(segment(comparison.creatives, attributeFilters), false),
      coverage: segmentCoverage(
        response.period.coverage,
        comparison.period.coverage,
        all,
        comparison.creatives,
        attributeFilters,
      ),
      period: { from: comparison.period.from, to: comparison.period.to },
    }
  }, [response, all, attributeFilters])
  // Under the «Не запущен» filter the minimums hide nothing: those rows have no spend and
  // no conversions to clear them with (GRO-187).
  const { shown: floored, hidden } = useMemo(
    () =>
      filters.verdict === 'notStarted'
        ? { shown: inSegment, hidden: [] }
        : floor(inSegment, { spend: filters.minSpend, conversions: filters.minConversions }),
    [inSegment, filters.minSpend, filters.minConversions, filters.verdict],
  )
  // After the floor and on the same terms: a display filter, so the rows it hides stay in
  // the segment and in its totals (GRO-64, D4). The floor's note counts only the floor.
  // Share is allocated over these rows, so the column the reader sees sums to 100.
  // The name search last, on the same terms as the verdict (GRO-165).
  const shown = useMemo(
    () => withShare(byName(byVerdict(floored, filters.verdict), filters.name)),
    [floored, filters.verdict, filters.name],
  )

  // The creatives picked for a bulk assignment, and the line the last write settled with.
  // A change of company, period, accounts, filter or view starts again from nothing: the
  // rows picked were picked from a list that is no longer the one on screen.
  const [picked, setPicked] = useState<ReadonlySet<string>>(NOTHING_PICKED)
  const [assignNotice, setAssignNotice] = useState<AssignNotice | null>(null)
  const pickScope = JSON.stringify([
    companyId, windowKey, accountKey, view, filters.hypothesisId, filters.techniqueId, filters.messageId,
    filters.offerId, filters.format,
    filters.audienceId, filters.minSpend, filters.minConversions, filters.verdict, filters.name,
  ])
  const [pickedUnder, setPickedUnder] = useState(pickScope)
  // A refetch of the same scope keeps the rows still on screen, the set select-all covers:
  // a creative that is in the new response but now outside a display filter leaves the
  // count and the next write with it.
  const [pickedFor, setPickedFor] = useState(response)
  if (pickedUnder !== pickScope) {
    setPickedUnder(pickScope)
    setPickedFor(response)
    setPicked(NOTHING_PICKED)
    setAssignNotice(null)
  } else if (pickedFor !== response) {
    setPickedFor(response)
    const onScreen = new Set(shown.map((row) => row.id))
    const kept = [...picked].filter((id) => onScreen.has(id))
    if (kept.length !== picked.size) setPicked(new Set(kept))
  }
  const pickedRef = useRef(picked)
  pickedRef.current = picked
  // The chip whose picker is open (GRO-170), and the axes a chip was opened for: each axis
  // is read once per dictionary revision however many rows the table has.
  const [openChip, setOpenChip] = useState<OpenChip | null>(null)
  const [wantedAxes, setWantedAxes] = useState<ReadonlySet<Axis>>(new Set())
  const pickerRevision = assignment?.pickerRevision ?? 0
  // The selection bar offers every axis, so a pick wants all four.
  const wants = (axis: Axis) => assignment !== undefined && (picked.size > 0 || wantedAxes.has(axis))
  const axisOptions = {
    idea: useDictionary(companyId, 'idea', pickerRevision, wants('idea'), fetchImpl),
    visual: useDictionary(companyId, 'visual', pickerRevision, wants('visual'), fetchImpl),
    message: useDictionary(companyId, 'message', pickerRevision, wants('message'), fetchImpl),
    offer: useDictionary(companyId, 'offer', pickerRevision, wants('offer'), fetchImpl),
  }
  const companyRef = useRef(companyId)
  companyRef.current = companyId
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  // The count for the nav belongs to the shell, which reads the scope itself (GRO-177), so
  // the screen reports nothing: the reading floors, the verdict and the name search are
  // view state and must not move a badge. `shown` and `answersScope` stay for the export,
  // the bulk-pick gate, the no-delivery line and the empty state's way to another period.
  const answersScope =
    !settling
    && load.status === 'ready'
    && servedFor !== null
    && servedFor.scope === scope
    && servedFor.audienceId === filters.audienceId

  // The panel's subject left the segment, which is what the design asks the screen to
  // close on: a refetch or a scope change whose response no longer carries the id, and
  // equally a creative that comes back reassigned and so falls outside the active
  // hypothesis, technique or kind. Reading the response's rows instead let that second
  // case leave a panel open over a row no longer on the screen.
  //
  // The reading floor is a separate concern and deliberately not part of this. A row the
  // floor hides is still in the segment, still counted in its totals, and the reader
  // lowers the floor to see it again; closing the panel there would answer a narrowing
  // the reader can undo in one move.
  // After a merge the open panel moved to the survivor, whose share it opened with is the
  // single row's from before the merge. The first response after it carries the summed
  // row, and the panel takes its share from there (GRO-161).
  if (shareFollows !== null && shareFollows.from !== response) {
    setShareFollows(null)
    const row = inSegment.find((candidate) => candidate.id === shareFollows.id)
    if (row !== undefined && selected !== null && selected.id === shareFollows.id) {
      setSelected({ id: row.id, share: row.share })
    }
  }
  const selectedLeft = selected !== null && !inSegment.some((row) => row.id === selected.id)
  useEffect(() => {
    if (selectedLeft) setSelected(null)
  }, [selectedLeft])

  const changeFilters = useCallback((next: Partial<FilterState>) => {
    setFilters((previous) => ({ ...previous, ...next }))
  }, [])

  // A header click on the active column reverses it; on another column it sorts that
  // column the way that column is usually read, which for every measure is downwards.
  const handleSort = useCallback(
    (key: SortKey) => {
      setSort((previous) => {
        if (previous === key) {
          setDir((d) => (d === 'asc' ? 'desc' : 'asc'))
          return previous
        }
        const ascending: SortKey[] = ['name', 'hypothesis', 'technique', 'format']
        setDir(ascending.includes(key) ? 'asc' : 'desc')
        return key
      })
    },
    [],
  )

  const bucketLabel = bucket
    ? bucket.id === UNASSIGNED
      ? // The sentinel drills from the comparison on either attribute axis; the label is
        // the absence named, not a dimension lookup that would fall through to the raw id.
        bucket.axis === 'hypothesis'
          ? t.hypUnassigned
          : t.techUnassigned
      : labelOf(
          bucket.axis === 'hypothesis'
            ? ctx.dimensions.hypotheses
            : bucket.axis === 'technique'
              ? ctx.dimensions.techniques
              : ctx.dimensions.audiences,
          bucket.id,
          lang,
        )
    : null

  // The note names the bucket only while the sole narrowing IS the bucket: the axis's
  // filter holds the bucket's value, sentinel included, and nothing else narrows. A
  // filter that replaces the bucket stands the note down instead of naming a bucket the
  // table no longer shows; re-selecting the bucket's value brings it back.
  const bucketFilterValue = bucket
    ? bucket.axis === 'hypothesis'
      ? filters.hypothesisId
      : bucket.axis === 'technique'
        ? filters.techniqueId
        : filters.audienceId
    : null
  const bucketAlone = bucket !== null && bucketFilterValue === bucket.id && narrowingCount(filters) === 1

  const focusMatches = focus ? inSegment.filter((row) => row.verdict?.key === focus).length : 0

  if (noSelection) {
    return (
      <Stack gap="lg" data-testid="creatives-screen">
        <EmptyState kind="selection" t={t} />
      </Stack>
    )
  }

  if (load.status === 'error') {
    return (
      <Stack gap="lg" data-testid="creatives-screen">
        <Card p="lg" role="alert" data-testid="creatives-error" data-code={load.code}>
          <Stack gap="sm">
            <Text size="md" fw={700}>{t.loadFailed}</Text>
            <Text size="sm" className="grv-muted">{errorCopy(load, t)}</Text>
            {load.code === 'unknown_audience' && filters.audienceId !== null ? (
              // A retry would ask for the same audience again; the way out is dropping it,
              // and the filter bar that holds it is not on this state.
              <Group>
                <Button onClick={() => changeFilters({ audienceId: null })}>{t.clearAudience}</Button>
              </Group>
            ) : SHELL_FIXABLE.has(load.code ?? '') ? (
              // A retry would ask for the same selection again. Every account of the
              // client is the server's default scope, which it never refuses.
              accountToggles && (
                <Group>
                  <Button onClick={accountToggles.onSelectAll} data-testid="select-all-accounts">{t.accSelectAll}</Button>
                </Group>
              )
            ) : (
              <Group>
                <Button onClick={() => setAttempt((n) => n + 1)}>{t.retry}</Button>
              </Group>
            )}
          </Stack>
        </Card>
      </Stack>
    )
  }

  if (!response) {
    return (
      <Stack gap="lg" data-testid="creatives-screen">
        <Group gap="sm" data-testid="creatives-loading">
          <Loader size="sm" />
          <Text size="sm">{t.loadingCreatives}</Text>
        </Group>
      </Stack>
    )
  }

  // Under a partial scope an empty list is not "nothing delivered" or "nothing matches":
  // the scope notices say what is missing, over the empty table (D8, C7, C8).
  const empty: EmptyKind | null =
    response.accounts.length === 0
      ? 'company'
      : partialScope
        ? null
        : response.creatives.length === 0
          // An empty response under an active filter is the filter's doing as much as the
          // period's, so it is told as filtered and offers no period change.
          ? (narrowingCount(filters) > 0 ? 'filtered' : 'creatives')
          : shown.length === 0
            ? 'filtered'
            : null

  // The design asked for the tiles to dim while a refetch is in flight. They do not: the
  // tiles are ink-6 on the surface, which clears 4.5:1 with nothing to spare, and any
  // opacity that reads as dimmed puts them under the floor G8 makes binding. The loading
  // notice and aria-busy carry the same information and stay legible.
  const loading = load.status === 'loading'
  const sorted = sortRows(shown, sort, dir, sortLabels(ctx))

  // Read from the rendered response, not the company's account list, so the notice and the
  // rows describe the same fetch. A demo or pulled account in scope leaves the partial marks
  // to say the rest.
  const scoped = response.accounts.filter((a) => response.scope.accountIds.includes(a.id))
  const unpulled = scoped.length > 0 && scoped.every((a) => !a.reported)

  // The screen's own open handler, passed to the table and the grid. The share comes from
  // the clicked row's allocation over the current segment, not over the rows on screen:
  // the list snapshot the panel was opened from, stated as the screen's last fetch.
  const openCreative = (id: string) => {
    const row = inSegment.find((candidate) => candidate.id === id)
    if (!row) return
    setSelected({ id, share: row.share })
  }

  // The export answers the same question the count does: are the rows on screen the ones
  // the scope selected now asked for? While a period, account or audience change loads,
  // the previous rows stay up but `ctx` already names the new scope, and a file written
  // then would carry one window's figures under another's name (GRO-64, D7).
  const canExport = answersScope && empty === null && sorted.length > 0

  // Selection and assignment answer the rows the current scope asked for, as the export
  // does, and wait while any write for this company is out so two never overlap.
  const canPick = assignment !== undefined && answersScope && !assignment.pending
  const togglePick = (id: string) => {
    if (!canPick) return
    const next = new Set(picked)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setPicked(next)
    setAssignNotice(null)
  }
  const pickAll = (ids: ReadonlySet<string>) => {
    if (!canPick) return
    setPicked(ids)
    setAssignNotice(null)
  }
  // The write settles here only while the company and the selection are the ones it was
  // submitted with; a later selection, or another company, is left as the reader made it.
  const submitTags = async (change: TagChange, done: string) => {
    if (assignment === undefined || !canPick || picked.size === 0) return
    const submitted = { companyId, picked }
    const dictCopy = DICTIONARY_COPY[lang]
    setAssignNotice(null)
    const outcome = await assignment.onAssign(companyId, [...picked], change)
    if (!mounted.current || companyRef.current !== submitted.companyId || pickedRef.current !== submitted.picked) return
    if (outcome.status === 'ok') {
      setPicked(NOTHING_PICKED)
      setAssignNotice({ role: 'status', text: fill(done, { c: outcome.body.updated }) })
      return
    }
    const line = failureLine(outcome, refusalCopy(dictCopy, changedAxes(change)))
    if (line !== null) setAssignNotice({ role: 'alert', text: line })
  }
  // The same guards and settling as an assignment.
  const submitVersion = async (version: string | null) => {
    if (assignment === undefined || !canPick || picked.size === 0) return
    const submitted = { companyId, picked }
    const dictCopy = DICTIONARY_COPY[lang]
    setAssignNotice(null)
    const outcome = await assignment.onSetVersion(companyId, [...picked], version)
    if (!mounted.current || companyRef.current !== submitted.companyId || pickedRef.current !== submitted.picked) return
    if (outcome.status === 'ok') {
      setPicked(NOTHING_PICKED)
      setAssignNotice({
        role: 'status',
        text: version === null
          ? fill(dictCopy.versionCleared, { c: outcome.body.updated })
          : fill(dictCopy.versionSet, { v: version, c: outcome.body.updated }),
      })
      return
    }
    const line = failureLine(outcome, dictCopy)
    if (line !== null) setAssignNotice({ role: 'alert', text: line })
  }
  // The merge dialog (GRO-161): open over the picked rows, with the line its last attempt
  // left. A refusal keeps it open; `assignment_conflict` means the assignments changed
  // under it, so the list is read again and the dialog offers the new ones.
  const mergeRows = sorted.filter((row) => picked.has(row.id))
  // A selection that dropped under two rows, or a switch to the grid, leaves nothing to merge.
  const mergeOpen = merging !== null && view === 'table' && mergeRows.length >= 2
  const openMerge = () => {
    if (!canPick || picked.size < 2) return
    setAssignNotice(null)
    setMerging({ line: null, saving: false })
  }
  const submitMerge = async (keep: MergeKeep | null) => {
    if (assignment === undefined || picked.size < 2) return
    const submitted = { companyId, picked }
    const dictCopy = DICTIONARY_COPY[lang]
    setMerging({ line: null, saving: true })
    const outcome = await assignment.onMerge(companyId, [...picked], keep)
    if (!mounted.current || companyRef.current !== submitted.companyId || pickedRef.current !== submitted.picked) return
    if (outcome.status === 'ok') {
      const survivor = outcome.body.creativeId
      if (selected !== null && submitted.picked.has(selected.id)) {
        setSelected({ id: survivor, share: selected.share })
        setShareFollows({ id: survivor, from: response })
      }
      setMerging(null)
      setPicked(NOTHING_PICKED)
      setAssignNotice({ role: 'status', text: fill(dictCopy.mergedDone, { c: submitted.picked.size }) })
      return
    }
    if (outcome.status === 'invalid' && outcome.code === 'assignment_conflict') setAttempt((n) => n + 1)
    setMerging({ line: failureLine(outcome, dictCopy), saving: false })
  }
  // A created value whose assignment did not land is named, so the reader knows it exists
  // before choosing it in the picker.
  const creationNotice = (result: CreateOutcome, name: string, axis: Axis): AssignNotice | null => {
    const dictCopy = DICTIONARY_COPY[lang]
    const lines = axisCopy(dictCopy, axis)
    if (result.stage === 'create') {
      const line = result.outcome.status === 'unreachable'
        ? fill(lines.createUncertain, { name })
        : failureLine(result.outcome, dictCopy)
      return line === null ? null : { role: 'alert', text: line }
    }
    const { outcome } = result
    if (outcome.status === 'ok') return { role: 'status', text: fill(lines.createdAssigned, { name, c: outcome.body.updated }) }
    if (outcome.status === 'unreachable') return { role: 'alert', text: fill(lines.createdAssignUncertain, { name }) }
    return failureLine(outcome, dictCopy) === null ? null : { role: 'alert', text: fill(lines.createdNotAssigned, { name }) }
  }
  // "Create" in a field of the selection bar: the value goes to the field and nothing is
  // assigned until «Применить». A refusal leaves the line in the bar.
  const createValue = async (axis: Axis, name: string): Promise<HypothesisValue | null> => {
    if (assignment === undefined || !canPick || picked.size === 0) return null
    const submitted = { companyId, picked }
    const dictCopy = DICTIONARY_COPY[lang]
    setAssignNotice(null)
    const outcome = await assignment.onCreateValue(companyId, name, axis)
    if (!mounted.current || companyRef.current !== submitted.companyId || pickedRef.current !== submitted.picked) return null
    if (outcome.status === 'ok') return outcome.body
    const line = outcome.status === 'unreachable'
      ? fill(axisCopy(dictCopy, axis).createUncertain, { name })
      : failureLine(outcome, dictCopy)
    if (line !== null) setAssignNotice({ role: 'alert', text: line })
    return null
  }
  const cancelSelection = () => {
    if (!canPick) return
    setPicked(NOTHING_PICKED)
    setAssignNotice(null)
  }
  // In-cell assignment (GRO-170): one creative and one axis, through the same pending write
  // and notice line as the assign bar. The chip shows the new value once the list refetches.
  // The selection is the reader's and stays as it is.
  const openTagChip = (chip: OpenChip) => {
    if (!canPick) return
    const options = axisOptions[chip.axis]
    if (options.phase === 'failed') options.retry()
    setWantedAxes((prev) => (prev.has(chip.axis) ? prev : new Set([...prev, chip.axis])))
    setOpenChip(chip)
  }
  const assignChip = async (rowId: string, axis: Axis, id: string | null) => {
    setOpenChip(null)
    if (assignment === undefined || !canPick) return
    const submitted = companyId
    const dictCopy = DICTIONARY_COPY[lang]
    setAssignNotice(null)
    const outcome = await assignment.onAssign(companyId, [rowId], tagChange(axis, id))
    if (!mounted.current || companyRef.current !== submitted) return
    if (outcome.status === 'ok') {
      const lines = axisCopy(dictCopy, axis)
      setAssignNotice({ role: 'status', text: fill(id === null ? lines.clearedDone : lines.assignedDone, { c: outcome.body.updated }) })
      return
    }
    const line = failureLine(outcome, refusalCopy(dictCopy, [axis]))
    if (line !== null) setAssignNotice({ role: 'alert', text: line })
  }
  const createChip = async (rowId: string, axis: Axis, name: string) => {
    setOpenChip(null)
    if (assignment === undefined || !canPick) return
    const submitted = companyId
    setAssignNotice(null)
    const result = await assignment.onCreateAndAssign(companyId, [rowId], name, axis)
    if (!mounted.current || companyRef.current !== submitted) return
    const notice = creationNotice(result, name, axis)
    if (notice !== null) setAssignNotice(notice)
  }
  // A decision on a suggestion chip (GRO-173), through the same pending write and notice
  // line. A suggestion settled or an axis filled elsewhere says so and refetches, so the
  // chip goes; a vanished one only refetches.
  const decideChip = async (rowId: string, suggestion: Suggestion, decision: Decision) => {
    setOpenChip(null)
    if (assignment === undefined || !canPick) return
    const submitted = companyId
    setAssignNotice(null)
    const outcome = await assignment.onDecide(companyId, rowId, suggestion.id, decision)
    if (!mounted.current || companyRef.current !== submitted) return
    if (outcome.status === 'ok') return
    if (outcome.status === 'settled' || outcome.status === 'axisAssigned' || outcome.status === 'gone') {
      if (outcome.status !== 'gone') {
        setAssignNotice({ role: 'alert', text: outcome.status === 'settled' ? t.suggestionSettled : t.suggestionAxisAssigned })
      }
      setAttempt((n) => n + 1)
      return
    }
    const line = failureLine(outcome, DICTIONARY_COPY[lang])
    if (line !== null) setAssignNotice({ role: 'alert', text: line })
  }
  const exportRows = () => sorted
  const exportTable = () => {
    if (!canExport) return
    downloadText(csvFileName(ctx, new Date(), period), toCsv(exportRows(), visible, ctx))
  }
  const exportWorkbook = () => {
    if (!canExport) return
    const workbook = toXlsx(exportCells(exportRows(), visible, ctx), t.xlsxSheetName)
    downloadBlob(exportFileName(ctx, new Date(), period, 'xlsx'), workbook, XLSX_MIME)
  }

  const toggleColumn = (key: ColumnKey) => {
    const next = new Set(hiddenColumns)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    changeHiddenColumns(next)
  }

  // The retained list answers the panel's own request: the same scope and audience, and a
  // body whose echo matches the period and the comparison asked for. While a new list is
  // in flight it does not, and the panel waits rather than reconciling with old dates.
  const listAnswers =
    load.status === 'ready'
    && servedFor !== null
    && servedFor.scope === scope
    && servedFor.audienceId === filters.audienceId
    && matchesEcho({ period, compare: response.contract === 'old' ? false : compare }, response)

  const detailParams: CreativesParams = {
    companyId,
    period,
    accountIds,
    audienceId: filters.audienceId,
    // The list's evaluation instant, so the panel's verdict reads the same 90-day reference.
    asOf: response?.asOf ?? null,
  }

  // A widen on a preset selects the longer preset; on a range it widens backwards from
  // the range's own end, so the widened window always contains the one the reader chose.
  const widen = onWidenPeriod
    ? (target: 30 | 90) =>
        onWidenPeriod(
          period.kind === 'preset'
            ? { kind: 'preset', days: target }
            : { kind: 'range', from: addDays(period.to, -(target - 1)), to: period.to },
        )
    : undefined
  const canWiden = (target: 30 | 90) =>
    period.kind === 'preset'
    || bounds === null
    || bounds.earliest === null
    || addDays(period.to, -(target - 1)) >= bounds.earliest

  const barShown = assignment !== undefined && view === 'table' && empty === null && picked.size > 0
  const allPicked = sorted.length > 0 && sorted.every((row) => picked.has(row.id))

  return (
    <Stack gap="lg" className="grv-creatives-screen" data-testid="creatives-screen" aria-busy={loading}>
      {loading && (
        <Group gap="sm" data-testid="creatives-loading">
          <Loader size="sm" />
          <Text size="sm">{t.loadingCreatives}</Text>
        </Group>
      )}

      {rangeRefused && (
        <Text size="sm" role="status" data-testid="range-refused">{t.periodRangeRefused}</Text>
      )}

      {unpulled && (
        <Text size="sm" data-testid="unpulled-notice">
          {t.unpulledNotice}
        </Text>
      )}

      {answersScope && <NoDeliveryLine response={response} t={t} />}
      <ScopeNotices response={response} t={t} />
      {onOpenSettings !== undefined && <TargetHint response={response} lang={lang} onOpen={onOpenSettings} />}

      {/* An empty company has no account to describe, so it gets no tiles: six zeroes
          would read as an account that spent nothing rather than as no account. */}
      {empty !== 'company' && <TotalsRow totals={totals} ctx={ctx} comparison={totalsComparison} scopePartial={partialScope} />}

      {empty === 'company' ? (
        <EmptyState kind="company" t={t} />
      ) : (
        <>
          <CreativesFilterBar
            ctx={ctx}
            all={all}
            filters={filters}
            onChange={changeFilters}
            view={view}
            onView={setView}
            sort={sort}
            dir={dir}
            onSort={handleSort}
            hidden={hiddenColumns}
            onToggleColumn={toggleColumn}
            onShowAllColumns={() => changeHiddenColumns(new Set())}
            accountToggles={accountToggles}
            onExport={exportTable}
            onExportXlsx={exportWorkbook}
            canExport={canExport}
            resultCount={fill(t.resultCount, {
              c: shown.length,
              a: shown.reduce((total, row) => total + row.ads.length, 0),
            })}
          />

          <Notes
            ctx={ctx}
            bucket={bucket}
            bucketLabel={bucketLabel}
            bucketCount={inSegment.length}
            bucketAlone={bucketAlone}
            onBucketBack={onCompareHypotheses}
            focus={focus}
            focusMatches={focusMatches}
            focusTotal={inSegment.length}
            hidden={hidden}
            hiddenSpend={sumMoney(hidden.map((row) => row.metrics.spend ?? 0))}
            segmentTotal={inSegment.length}
            onClearFloor={() => changeFilters({ minSpend: 0, minConversions: 0 })}
          />

          <ThinBanner ctx={ctx} totals={totals} onCompareHypotheses={onCompareHypotheses} />
          <Definitions ctx={ctx} />

          {/* Outside the table branch: a write that clears the selection, or empties the bucket
              on screen, unmounts the bar, and its line has to outlive it. */}
          {assignNotice !== null && !barShown && (
            <Text size="sm" role={assignNotice.role} data-testid="assign-notice">{assignNotice.text}</Text>
          )}

          {empty !== null ? (
            <EmptyState kind={empty} t={t} onChangePeriod={answersScope ? onOpenPeriod : undefined} />
          ) : view === 'table' ? (
            <>
              {assignment !== undefined && (
                <Checkbox
                  label={DICTIONARY_COPY[lang].selectAll}
                  checked={allPicked}
                  indeterminate={!allPicked && sorted.some((row) => picked.has(row.id))}
                  disabled={!canPick || sorted.length === 0}
                  onChange={() => pickAll(allPicked ? NOTHING_PICKED : new Set(sorted.map((row) => row.id)))}
                  data-testid="select-all-creatives"
                />
              )}
              <CreativesTable
                ctx={ctx}
                rows={sorted}
                columns={visible}
                sort={sort}
                dir={dir}
                onSort={handleSort}
                onOpenCreative={openCreative}
                showSamples={filters.showSamples}
                onWidenPeriod={widen}
                canWiden={canWiden}
                selection={assignment === undefined ? undefined : {
                  picked,
                  enabled: canPick,
                  onToggle: togglePick,
                  label: (row) => fill(DICTIONARY_COPY[lang].selectRow, { name: row.displayName }),
                }}
                tagging={assignment === undefined ? undefined : {
                  enabled: canPick,
                  open: openChip,
                  onOpen: openTagChip,
                  onClose: () => setOpenChip(null),
                  values: (axis) => axisOptions[axis].values,
                  fresh: (axis) => axisOptions[axis].fresh,
                  phase: (axis) => axisOptions[axis].phase,
                  retry: (axis) => axisOptions[axis].retry(),
                  onChoose: (rowId, axis, id) => { void assignChip(rowId, axis, id) },
                  onCreate: (rowId, axis, name) => { void createChip(rowId, axis, name) },
                  onDecide: (rowId, suggestion, decision) => { void decideChip(rowId, suggestion, decision) },
                }}
              />
              {barShown && assignment !== undefined && (
                <SelectionBar
                  key={companyId}
                  lang={lang}
                  count={picked.size}
                  enabled={canPick}
                  options={axisOptions}
                  onApply={(change) => { void submitTags(change, DICTIONARY_COPY[lang].appliedDone) }}
                  onClearIdea={() => { void submitTags({ ideaId: null }, DICTIONARY_COPY[lang].clearedDone) }}
                  onClearTags={() => {
                    void submitTags({ visualId: null, messageId: null, offerId: null }, DICTIONARY_COPY[lang].tagsClearedDone)
                  }}
                  onCreate={createValue}
                  versionable={sorted.every((row) => !picked.has(row.id) || row.hypothesisId !== null)}
                  onSetVersion={(version) => { void submitVersion(version) }}
                  onMerge={openMerge}
                  onCancel={cancelSelection}
                  notice={assignNotice}
                />
              )}
            </>
          ) : (
            <CreativesGrid
              ctx={ctx}
              rows={sorted}
              sort={sort}
              dir={dir}
              onOpenCreative={openCreative}
            />
          )}

          {selected !== null && (
            <CreativeDetailPanel
              id={selected.id}
              ctx={ctx}
              share={selected.share}
              params={detailParams}
              listWindow={{ from: response.period.from, to: response.period.to }}
              listContract={response.contract}
              listPending={!listAnswers}
              onStaleList={() => setAttempt((n) => n + 1)}
              onOldContract={onOldContract}
              onClose={() => setSelected(null)}
              reloadKey={reloadKey}
              suspended={suspended || mergeOpen}
              fetchImpl={fetchImpl}
            />
          )}

          {mergeOpen && merging !== null && (
            <MergeDialog
              lang={lang}
              ctx={ctx}
              rows={mergeRows}
              assignments={distinctAssignments(mergeRows)}
              line={merging.line}
              saving={merging.saving}
              onConfirm={(keep) => { void submitMerge(keep) }}
              onCancel={() => setMerging(null)}
            />
          )}
        </>
      )}
    </Stack>
  )
}

/**
 * The no-target hint (GRO-185): while the company has no target for its primary metric,
 * verdicts compare with the 90-day average, and the hint points an Owner or an Admin to the
 * settings that set one. It does not show for an empty list or for a period outside the
 * reporting currency, where a target would not change what is on screen.
 */
function TargetHint({ response, lang, onOpen }: { response: CreativesResponse; lang: Lang; onOpen: () => void }) {
  const basis = response.verdictBasis
  if (basis === null || basis.targetSet || basis.outsideReporting || response.creatives.length === 0) return null
  const copy = SETTINGS_COPY[lang]
  return (
    <div data-testid="settings-hint">
      <AlertBanner
        tone="info"
        action={<Button variant="subtle" size="xs" onClick={onOpen} data-testid="settings-hint-open">{copy.hintLink} →</Button>}
      >
        {fill(copy.hint, { metric: basis.metric === 'cpa' ? 'CPA' : 'ROAS' })}
      </AlertBanner>
    </div>
  )
}
